// pages/RSVPPage.tsx
import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { CheckCircle, X, Plus, Trash2 } from "lucide-react";
import leftFlower from "../assets/leftFlower.png";
import rightFlower from "../assets/rightFlower.png";
import { useOrderTicket } from "../lib/api/ticket";

interface Attendee {
  id: number;
  fullName: string;
  email: string;
}

const RSVPPage = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const [attendees, setAttendees] = useState<Attendee[]>([
    { id: 1, fullName: "", email: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);

  const MAX_ATTENDEES = 5;

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const handleChange = (
    id: number,
    field: "fullName" | "email",
    value: string
  ) => {
    setAttendees((prev) =>
      prev.map((attendee) =>
        attendee.id === id ? { ...attendee, [field]: value } : attendee
      )
    );
  };

  const addAttendee = () => {
    if (attendees.length < MAX_ATTENDEES) {
      const newId = Math.max(...attendees.map((a) => a.id)) + 1;
      setAttendees((prev) => [...prev, { id: newId, fullName: "", email: "" }]);
    }
  };

  const removeAttendee = (id: number) => {
    if (attendees.length > 1) {
      setAttendees((prev) => prev.filter((attendee) => attendee.id !== id));
    }
  };

  const { mutateAsync: orderTicket } = useOrderTicket();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit all attendees
      const promises = attendees.map((attendee) =>
        orderTicket({
          fullName: attendee.fullName,
          email: attendee.email,
        })
      );

      const results = await Promise.all(promises);

      // Check if all submissions were successful
      const allSuccessful = results.every((res) => res?.statusCode === 201);

      if (allSuccessful) {
        setSubmittedCount(attendees.length);
        setShowSuccessDialog(true);
        setAttendees([{ id: 1, fullName: "", email: "" }]);
      }
    } catch (error) {
      console.error("RSVP submission failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setShowSuccessDialog(false);
  };

  const isFormValid = attendees.every(
    (attendee) =>
      attendee.fullName.trim() !== "" && attendee.email.trim() !== ""
  );

  return (
    <>
      <section className="w-full bg-white pt-10 pb-16 px-6 min-h-[80vh] flex items-center justify-center relative">
        {/* Decorative floral elements - Right top (BEHIND content) */}
        <img
          src={rightFlower}
          alt=""
          className="absolute right-0 top-0 w-32 md:w-60 opacity-80 pointer-events-none z-0"
        />

        {/* Decorative floral elements - Left bottom (BEHIND content) */}
        <img
          src={leftFlower}
          alt=""
          className="absolute left-0 bottom-[-0%] w-32 md:w-60 opacity-80 pointer-events-none z-0"
        />

        <div
          data-aos="fade-up"
          className="w-full max-w-2xl bg-[#f5f5f5] rounded-2xl px-6 py-12 md:px-12 md:py-16 relative z-10"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1
              className="text-2xl md:text-3xl bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent mb-4"
              style={{
                // fontFamily: "'Times New Roman', Times, serif",
                fontWeight: 400,
              }}
            >
              RSVP
            </h1>
            <p className="text-gray-600 leading-relaxed">
              Please enter the details of everyone attending from your family.
            </p>
          </div>

          {/* Attendee Limit Info */}
          {/* <div className="flex items-center justify-center gap-2 mb-8 py-3 px-4 bg-white rounded-lg border border-gray-200">
            <Users size={18} className="text-[#C49355]" />
            <p className="text-gray-600 text-sm">
              Maximum of{" "}
              <span className="font-semibold text-[#C49355]">
                {MAX_ATTENDEES} guests
              </span>{" "}
              per family
            </p>
          </div> */}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Attendee Cards */}
            {attendees.map((attendee, index) => (
              <div
                key={attendee.id}
                className="bg-white rounded-xl p-6 border border-gray-200 relative"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {/* Attendee Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-700">
                    Guest {index + 1}
                    {index === 0 && (
                      <span className="ml-2 text-xs text-[#C49355]">
                        (Primary Contact)
                      </span>
                    )}
                  </h3>

                  {/* Remove Button - only show if more than 1 attendee */}
                  {attendees.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAttendee(attendee.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      title="Remove guest"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                {/* Full Name Input */}
                <div className="mb-4">
                  <label className="block text-xs text-gray-500 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={attendee.fullName}
                    onChange={(e) =>
                      handleChange(attendee.id, "fullName", e.target.value)
                    }
                    placeholder="First and Last name"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C49355] focus:bg-white transition-all rounded-lg"
                  />
                </div>

                {/* Email Input */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={attendee.email}
                    onChange={(e) =>
                      handleChange(attendee.id, "email", e.target.value)
                    }
                    placeholder="email@example.com"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C49355] focus:bg-white transition-all rounded-lg"
                  />
                </div>
              </div>
            ))}

            {/* Add Attendee Button */}
            {attendees.length < MAX_ATTENDEES && (
              <button
                type="button"
                onClick={addAttendee}
                className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#C49355] hover:text-[#C49355] transition-all flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                <span>Add Another Guest</span>
                {/* <span className="text-xs">
                  ({attendees.length}/{MAX_ATTENDEES})
                </span> */}
              </button>
            )}

            {/* Max Reached Message */}
            {attendees.length >= MAX_ATTENDEES && (
              <p className="text-center text-sm text-gray-500 py-2">
                Maximum number of guests reached
              </p>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid}
                className="w-full bg-[#3d3d3d] hover:bg-[#2d2d2d] disabled:bg-[#9d9d9d] disabled:cursor-not-allowed text-white py-4 text-sm tracking-wider transition-colors duration-300 rounded-lg"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting {attendees.length} Guest
                    {attendees.length > 1 ? "s" : ""}...
                  </span>
                ) : (
                  `Submit RSVP for ${attendees.length} Guest${
                    attendees.length > 1 ? "s" : ""
                  }`
                )}
              </button>
            </div>

            {/* Helper Text */}
            <p className="text-center text-xs text-gray-400 mt-4">
              Each guest will receive a confirmation email
            </p>
          </form>
        </div>
      </section>

      {/* Success Dialog Overlay */}
      {showSuccessDialog && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={closeDialog}
        >
          {/* Dialog Box */}
          <div
            className="bg-white rounded-2xl p-8 md:p-12 max-w-md w-full text-center relative animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeDialog}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>

            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#C49355] to-[#E8B46A] flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Title */}
            <h2
              className="text-2xl md:text-3xl bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent mb-4"
              style={{
                // fontFamily: "'Times New Roman', Times, serif",
                fontWeight: 400,
              }}
            >
              Thank You!
            </h2>

            {/* Message */}
            <p
              className="text-gray-600 mb-2"
              style={{
                fontFamily: "'Times New Roman', Times, serif",
              }}
            >
              {submittedCount} guest{submittedCount > 1 ? "s have" : " has"}{" "}
              been registered.
            </p>
            <p
              style={{
                fontFamily: "'Times New Roman', Times, serif",
              }}
              className="text-gray-600 mb-8"
            >
              We can't wait to celebrate with you!
            </p>

            {/* Wedding Date */}
            <div className="border-t border-gray-200 pt-6">
              <p className="text-gray-500 text-sm mb-1">See you on</p>
              <p className="text-xl bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent">
                April 22, 2026
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={closeDialog}
              className="mt-8 bg-[#3d3d3d] hover:bg-[#2d2d2d] text-white px-12 py-3 text-sm transition-colors duration-300 rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default RSVPPage;
